import { motion } from "framer-motion";
import { Play } from "lucide-react";

/**
 * IPhoneFrame — Apple-style iPhone bezel with a custom poster + play button.
 *
 *   • Floating titanium bezel with dynamic-island pill at the top
 *   • Subtle reflective sheen across the glass
 *   • Animated gradient placeholder behind a centred Play overlay
 *   • Click anywhere on the screen fires `onPlay` — the parent slide opens
 *     a portrait lightbox with the actual video
 *
 * We don't embed the video inside the iPhone any more — full-screen
 * lightbox playback is a much better stage moment.
 */
export default function IPhoneFrame({
  width = 340,
  onPlay,
  posterUrl,
  testId = "iphone-frame",
}) {
  const aspect = 19.5 / 9;
  const innerWidth = width - 16; // bezel
  const innerHeight = innerWidth * aspect;
  const totalHeight = innerHeight + 16;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.92, filter: "blur(20px)" }}
      animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
      transition={{ duration: 1.6, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}
      style={{ width, height: totalHeight }}
      className="relative pointer-events-auto"
      data-testid={testId}
    >
      {/* Soft glow under device */}
      <div
        aria-hidden
        className="absolute inset-0 rounded-[60px] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(150,193,31,0.45) 0%, rgba(0,222,182,0.30) 35%, transparent 70%)",
          filter: "blur(40px)",
          transform: "scale(1.18)",
          opacity: 0.85,
        }}
      />

      {/* Outer titanium bezel */}
      <div
        className="relative w-full h-full rounded-[60px] overflow-hidden"
        style={{
          background:
            "linear-gradient(160deg, #2a2a2c 0%, #0c0c0d 50%, #2a2a2c 100%)",
          border: "1px solid rgba(255,255,255,0.10)",
          boxShadow:
            "0 50px 120px -30px rgba(0,0,0,0.85), 0 0 0 1px rgba(255,255,255,0.06) inset, 0 1px 0 rgba(255,255,255,0.10) inset",
          padding: 8,
        }}
      >
        <button
          type="button"
          onClick={onPlay}
          className="relative block w-full h-full rounded-[52px] overflow-hidden cursor-pointer group"
          style={{
            background:
              "linear-gradient(135deg, rgba(7,18,12,1) 0%, rgba(20,30,24,1) 100%)",
          }}
          aria-label="Play intro video"
          data-testid="iphone-screen"
        >
          {/* Poster image — sits under the gradient and overlay */}
          {posterUrl && (
            <img
              src={posterUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          )}

          {/* Animated placeholder gradient (still drifts behind the poster) */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            animate={{
              background: [
                "radial-gradient(ellipse at 30% 30%, rgba(150,193,31,0.40) 0%, rgba(7,18,12,0.6) 60%)",
                "radial-gradient(ellipse at 70% 70%, rgba(0,222,182,0.40) 0%, rgba(7,18,12,0.6) 60%)",
                "radial-gradient(ellipse at 30% 70%, rgba(212,148,10,0.40) 0%, rgba(7,18,12,0.6) 60%)",
                "radial-gradient(ellipse at 30% 30%, rgba(150,193,31,0.40) 0%, rgba(7,18,12,0.6) 60%)",
              ],
            }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            style={{ opacity: posterUrl ? 0.18 : 1, mixBlendMode: "screen" }}
          />

          {/* Bottom-up scrim so the play button reads against the poster */}
          {posterUrl && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse at center, rgba(0,0,0,0.20) 0%, rgba(0,0,0,0.55) 100%)",
              }}
            />
          )}

          {/* Reflective glass sheen */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none rounded-[52px]"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.10) 0%, transparent 35%, transparent 65%, rgba(255,255,255,0.04) 100%)",
              mixBlendMode: "screen",
            }}
          />

          {/* Dynamic-island pill */}
          <div
            aria-hidden
            className="absolute top-2.5 left-1/2 -translate-x-1/2 z-10"
            style={{
              width: 96,
              height: 28,
              borderRadius: 999,
              background: "#000",
              boxShadow: "inset 0 -1px 0 rgba(255,255,255,0.04)",
            }}
          />

          {/* Play overlay */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <motion.span
              className="absolute rounded-full"
              style={{
                width: 96,
                height: 96,
                border: "1.5px solid rgba(255,255,255,0.55)",
              }}
              animate={{ scale: [1, 1.6], opacity: [0.55, 0] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
            />
            <motion.span
              className="absolute rounded-full"
              style={{
                width: 96,
                height: 96,
                border: "1.5px solid rgba(150,193,31,0.55)",
              }}
              animate={{ scale: [1, 1.4], opacity: [0.85, 0] }}
              transition={{
                duration: 2.4,
                repeat: Infinity,
                delay: 0.6,
                ease: "easeOut",
              }}
            />

            <div
              className="relative w-[88px] h-[88px] rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
              style={{
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(232,235,232,0.85) 100%)",
                boxShadow:
                  "0 12px 40px rgba(0,0,0,0.4), 0 0 30px rgba(150,193,31,0.45)",
                backdropFilter: "blur(8px)",
              }}
            >
              <Play
                className="w-7 h-7"
                fill="#0d0d0f"
                style={{ color: "#0d0d0f", marginLeft: 4 }}
                strokeWidth={0}
              />
            </div>
          </div>
        </button>
      </div>
    </motion.div>
  );
}
